import { useCallback, useMemo, useState } from "react";
import { Button, Stack, Typography } from "@mui/material";
import { Add, ContentCopy, Delete, Edit } from "@mui/icons-material";
import { arrayMoveImmutable } from "array-move";
import { GridRowOrderChangeParams } from "@mui/x-data-grid-pro";
import {
  BusinessEntityType,
  BillingMethod,
  BillingRateParameter,
  BillingRateParameterType,
  BillingRule,
  BillingRuleCondition,
  BillingRuleConditionOperator,
  BillingRuleConditionTarget,
  BillingRuleType,
  GetCustomerDetailsQuery,
  OrderByDirection,
  useGetBusinessEntitiesQuery,
  useGetTractorListQuery,
  useGetTrailerListQuery,
  useGetCustomerBillingRulesQuery,
  useAddBillingRuleMutation,
  useEditBillingRuleMutation,
  useDeleteBillingRuleMutation,
} from "../../../graphql/generated";
import { useQueryClient } from "@tanstack/react-query";
import { TableField } from "../../common/LynksTable/LynksTable";
import SmartLynksTable from "../../common/SmartLynksTable/SmartLynksTable";
import { useTranslation } from "react-i18next";
import billingMethodLabel from "../../../utils/labels/billingRule/billingMethodLabel";
import billingRuleConditionOperatorLabel from "../../../utils/labels/billingRule/billingRuleConditionOperatorLabel";
import billingRuleConditionTargetLabel from "../../../utils/labels/billingRule/billingRuleConditionTargetLabel";
import enumLabel from "../../../utils/labels/enumLabel";
import { QueryFn } from "../../common/SmartLynksTable/SmartLynksTable";
import { formatCurrency } from "../../../utils/labels/formatCurrency";
import { formatMileage } from "../../../utils/labels/formatDistance";
import { isRateTaxable } from "../../../utils/billingRule/isRateTaxable";
import BillingRuleFormDialog from "./BillingRuleFormDialog";
import useDialog from "../../../utils/hooks/useDialog";
import { testIds } from "./BillingRulesEditor.testIds";

type BillingRulesEditorProps = {
  customer: GetCustomerDetailsQuery["customerById"];
  customerId: string;
  addBillingRuleMutation: ReturnType<typeof useAddBillingRuleMutation>;
  editBillingRuleMutation: ReturnType<typeof useEditBillingRuleMutation>;
  deleteBillingRuleMutation: ReturnType<typeof useDeleteBillingRuleMutation>;
  enqueueSnackbar: (message: any) => void;
};

export type BillingRuleItem = Omit<BillingRule, "condition">;

type BillingRuleGridRow = {
  _id: string;
  type: BillingRuleType;
  label?: string | null;
  referenceNumber?: string | null;
  effectiveStartDate?: string | null;
  effectiveEndDate?: string | null;
  isActive: boolean;
  priority?: number | null;
  conditions?: BillingRuleCondition[] | null;
  customFields?: Array<{ key: string; value?: any | null }> | null;
  rates?:
    | Array<{
        method?: BillingMethod | null;
        amount?: number | null;
        label?: string | null;
        referenceNumber?: string | null;
        taxable?: boolean | null;
        parameters?: BillingRateParameter[] | null;
      }>
    | null;
};

const formatValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map((item) => formatValue(item)).filter(Boolean).join(" and ");
  }
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return enumLabel(value) || value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return String(value);
};

const resolveConditionValue = (
  target: BillingRuleConditionTarget,
  value: unknown,
  lookups: {
    businessEntityById: Record<string, string>;
    trailerById: Record<string, string>;
    tractorById: Record<string, string>;
  }
): unknown => {
  const mapSingle = (singleValue: unknown): unknown => {
    if (
      target === BillingRuleConditionTarget.Mileage &&
      typeof singleValue === "number"
    ) {
      return formatMileage(singleValue, false);
    }
    if (typeof singleValue !== "string") {
      return singleValue;
    }
    if (
      target === BillingRuleConditionTarget.Shipper ||
      target === BillingRuleConditionTarget.Receiver
    ) {
      return lookups.businessEntityById[singleValue] || singleValue;
    }
    if (target === BillingRuleConditionTarget.Trailer) {
      return lookups.trailerById[singleValue] || singleValue;
    }
    if (target === BillingRuleConditionTarget.Tractor) {
      return lookups.tractorById[singleValue] || singleValue;
    }
    return singleValue;
  };

  if (Array.isArray(value)) {
    return value.map(mapSingle);
  }
  return mapSingle(value);
};

const formatCondition = (
  condition: BillingRuleCondition,
  lookups: {
    businessEntityById: Record<string, string>;
    trailerById: Record<string, string>;
    tractorById: Record<string, string>;
  }
): string => {
  if (condition.target === BillingRuleConditionTarget.Otherwise) {
    return billingRuleConditionTargetLabel(condition.target);
  }
  const targetLabel = billingRuleConditionTargetLabel(condition.target);
  const operatorLabel = billingRuleConditionOperatorLabel(
    condition.operator || BillingRuleConditionOperator.Equals
  );
  const conditionValue = formatValue(
    resolveConditionValue(condition.target, condition.value, lookups)
  );
  return conditionValue
    ? `${targetLabel} ${operatorLabel} ${conditionValue}`
    : `${targetLabel} ${operatorLabel}`;
};

const resolveRateParameterValue = (
  parameter: BillingRateParameter,
  businessEntityById: Record<string, string>
): unknown => {
  if (parameter.type !== BillingRateParameterType.StorageFacility) {
    return parameter.value;
  }

  const resolveStorageFacilityCompositeKey = (raw: unknown): unknown => {
    if (typeof raw !== "string") {
      return raw;
    }
    const [receiverId, storageFacilityIdentifier] = raw.split(":");
    if (!receiverId || !storageFacilityIdentifier) {
      return raw;
    }
    const receiverName = businessEntityById[receiverId];
    return receiverName
      ? `${storageFacilityIdentifier} (${receiverName})`
      : storageFacilityIdentifier;
  };

  if (Array.isArray(parameter.value)) {
    return parameter.value.map(resolveStorageFacilityCompositeKey);
  }
  return resolveStorageFacilityCompositeKey(parameter.value);
};

const formatRate = (
  rate: NonNullable<BillingRuleGridRow["rates"]>[number],
  businessEntityById: Record<string, string>
): string => {
  if (!rate.method) {
    return "";
  }
  const methodLabel = billingMethodLabel(rate.method).toLowerCase();
  const isPercentageMethod =
    rate.method === BillingMethod.PercentageOfLineHaul ||
    rate.method === BillingMethod.PercentageOfTotal;
  const amountLabel = isPercentageMethod
    ? `${Number(rate.amount || 0)}%`
    : formatCurrency(Number(rate.amount || 0));
  const methodPhrase =
    isPercentageMethod && methodLabel.startsWith("percentage of ")
      ? `of ${methodLabel.replace("percentage of ", "")}`
      : methodLabel;
  const parameterLabel = (rate.parameters || [])
    .map((parameter) =>
      formatValue(resolveRateParameterValue(parameter, businessEntityById))
    )
    .filter(Boolean)
    .join(", ");
  const extraLabel = rate.label || parameterLabel;
  return extraLabel
    ? `${amountLabel} ${methodPhrase}, ${extraLabel}`
    : `${amountLabel} ${methodPhrase}`;
};

const useBillingRulesGridQuery: QueryFn<
  BillingRuleGridRow,
  "billingRules",
  never,
  { customer: string }
> = (variables, queryOptions) =>
  useGetCustomerBillingRulesQuery(
    {
      ...variables,
      orderBy:
        variables.orderBy && variables.orderBy.length
          ? variables.orderBy
          : [{ field: "priority", direction: OrderByDirection.Asc }],
    },
    queryOptions
  );

const BillingRulesEditor = ({
  customer,
  customerId,
  addBillingRuleMutation,
  editBillingRuleMutation,
  deleteBillingRuleMutation,
  enqueueSnackbar,
}: BillingRulesEditorProps) => {
  const { t } = useTranslation("billingRules");
  const queryClient = useQueryClient();
  const { showDialog, hideDialog } = useDialog();
  const [dialogMode, setDialogMode] = useState<"create" | "edit" | "copy">("create");
  const [selectedRule, setSelectedRule] = useState<BillingRuleItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<BillingRuleType | null>(null);
  const [isReorderingLinehaul, setIsReorderingLinehaul] = useState(false);
  const [primaryPageRows, setPrimaryPageRows] = useState<
    BillingRuleGridRow[]
  >([]);

  const additionalQueryVariables = useMemo(
    () => ({ customer: customerId }),
    [customerId]
  );
  const businessEntitiesQuery = useGetBusinessEntitiesQuery(
    {
      businessEntityTypes: [
        BusinessEntityType.Shipper,
        BusinessEntityType.Receiver,
      ],
    },
    { keepPreviousData: true }
  );
  const trailersQuery = useGetTrailerListQuery(undefined, {
    keepPreviousData: true,
  });
  const tractorsQuery = useGetTractorListQuery(undefined, {
    keepPreviousData: true,
  });

  const businessEntityById = useMemo(
    () =>
      Object.fromEntries(
        (businessEntitiesQuery.data?.businessEntities.data || []).map(
          (entity) => [entity._id, entity.name]
        )
      ),
    [businessEntitiesQuery.data?.businessEntities.data]
  );
  const trailerById = useMemo(
    () =>
      Object.fromEntries(
        (trailersQuery.data?.trailers.data || []).map((trailer) => [
          trailer._id,
          trailer.serialNumber,
        ])
      ),
    [trailersQuery.data?.trailers.data]
  );
  const tractorById = useMemo(
    () =>
      Object.fromEntries(
        (tractorsQuery.data?.tractors.data || []).map((tractor) => [
          tractor._id,
          tractor.serialNumber,
        ])
      ),
    [tractorsQuery.data?.tractors.data]
  );
  const typeFilterValues = useMemo(
    () =>
      Object.values(BillingRuleType).map((type) => ({
        value: type,
        label: String(
          type === BillingRuleType.Primary
            ? t("lineHaul", "Line Haul")
            : type === BillingRuleType.Accessorial
            ? t("accessorial", "Accessorial")
            : type === BillingRuleType.Tax
            ? t("tax", "Tax")
            : type === BillingRuleType.OrderCancellation
            ? t("orderCancellation", "Truck Ordered Not Used")
            : enumLabel(type)
        ),
      })),
    [t]
  );

  const conditionTargetFilterValues = useMemo(
    () =>
      Object.values(BillingRuleConditionTarget).map((target) => ({
        value: target,
        label: billingRuleConditionTargetLabel(target),
      })),
    []
  );

  const methodFilterValues = useMemo(
    () =>
      Object.values(BillingMethod).map((method) => ({
        value: method,
        label: billingMethodLabel(method),
      })),
    []
  );

  const fields: Array<TableField<BillingRuleGridRow>> = useMemo(
    () => [
      {
        value: (rule) =>
          String(
            typeFilterValues.find((type) => type.value === rule.type)?.label ||
              enumLabel(rule.type) ||
              rule.type
          ),
        label: t("billingType", "Type"),
        type: "select",
        sortBy: "type",
        values: typeFilterValues,
      },
      {
        value: (rule) =>
          (
            <Stack spacing={0.25} sx={{ py: 0.75 }}>
              {(rule.conditions || []).map(
                (condition: BillingRuleCondition, index: number) => {
                const conditionText = formatCondition(condition, {
                  businessEntityById,
                  trailerById,
                  tractorById,
                });
                return (
                  <Typography key={`${conditionText}-${index}`} variant="body1">
                    {conditionText}
                  </Typography>
                );
                }
              )}
            </Stack>
          ),
        label: t("form.when", "Condition"),
        type: "select",
        sortBy: "conditions.target",
        values: conditionTargetFilterValues,
      },
      {
        value: (rule) =>
          (
            <Stack spacing={0.25} sx={{ py: 0.75 }}>
              {(rule.rates || [])
                .map((rate) => formatRate(rate, businessEntityById))
                .filter(Boolean)
                .map((rateText, index) => (
                  <Typography key={`${rateText}-${index}`} variant="body1">
                    {rateText}
                  </Typography>
                ))}
            </Stack>
          ),
        label: t("rates", "Rate"),
        type: "select",
        sortBy: "rates.method",
        values: methodFilterValues,
      },
      {
        value: (rule) =>
          (
            <Stack spacing={0.25} sx={{ py: 0.75 }}>
              {(rule.rates || [])
                .filter((rate) => rate.method)
                .map((rate, index) => (
                  <Typography key={`taxable-${index}`} variant="body1">
                    {isRateTaxable(rate, rule.type === BillingRuleType.Tax)
                      ? t("common:yes", "Yes")
                      : t("common:no", "No")}
                  </Typography>
                ))}
            </Stack>
          ),
        label: t("form.taxable", "Taxable"),
        type: "string",
        sortBy: "rates.taxable",
      },
      {
        value: (rule) => rule.effectiveStartDate,
        label: t("form.effectiveStart", "Start"),
        type: "date",
        sortBy: "effectiveStartDate",
        filterable: true,
      },
      {
        value: (rule) => rule.effectiveEndDate,
        label: t("form.effectiveEnd", "End"),
        type: "date",
        sortBy: "effectiveEndDate",
        filterable: true,
      },
      {
        value: "isActive",
        label: t("active", "Active"),
        type: "boolean",
        sortBy: "isActive",
      },
    ],
    [
      businessEntityById,
      conditionTargetFilterValues,
      methodFilterValues,
      trailerById,
      tractorById,
      t,
      typeFilterValues,
    ]
  );

  const mapGridRowToBillingRule = useCallback(
    (rule: BillingRuleGridRow): BillingRuleItem => ({
      _id: rule._id,
      amount: null,
      conditions: rule.conditions || [],
      customer: null,
      customFields: rule.customFields || [],
      effectiveEndDate: rule.effectiveEndDate || null,
      effectiveStartDate: rule.effectiveStartDate || null,
      isActive: rule.isActive,
      label: rule.label || "",
      method: null,
      priority: rule.priority || 0,
      rates: (rule.rates || []).map((rate) => ({
        method: rate.method || BillingMethod.FlatFee,
        amount: Number(rate.amount || 0),
        label: rate.label || "",
        referenceNumber: rate.referenceNumber || "",
        taxable: rate.taxable,
        parameters: rate.parameters || [],
      })),
      referenceNumber: rule.referenceNumber || "",
      type: rule.type,
    }),
    []
  );

  const handleRecordClick = useCallback(
    (rule: BillingRuleGridRow) => {
      setDialogMode("edit");
      setSelectedRule(mapGridRowToBillingRule(rule));
      setIsDialogOpen(true);
    },
    [mapGridRowToBillingRule]
  );

  const refetchBillingRules = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["getCustomerBillingRules"] });
  }, [queryClient]);

  const handleFilteredRecordsChange = useCallback(
    (filtered: BillingRuleGridRow[]) => {
      // Keep an up-to-date snapshot of the currently loaded rows (page subset),
      // so `onRowOrderChange` can correctly remap `priority` indices.
      setPrimaryPageRows(filtered);
    },
    []
  );

  const handleFilterModelChange = useCallback((model: any) => {
    const ruleTypeFilter = model.items.find(
      (item: any) => item.field === "type" && item.value
    )?.value as BillingRuleType | undefined;
    setSelectedTypeFilter(ruleTypeFilter || null);
  }, []);

  const renderHeaderActions = useCallback(
    () => (
      <Button
        data-testid={testIds.billingRulesEditorAddRule}
        variant="contained"
        startIcon={<Add />}
        onClick={() => {
          setDialogMode("create");
          setSelectedRule(null);
          setIsDialogOpen(true);
        }}
      >
        {t("addBillingRule", "Add Billing Rule")}
      </Button>
    ),
    [t]
  );

  // Row reordering is active only when filtering to PRIMARY and no save is in flight.
  // The reorder column is always rendered (rowReordering is always passed) so it
  // stays in a fixed position; it is just disabled when the condition is not met.
  const isLinehaulReorderingEnabled =
    selectedTypeFilter === BillingRuleType.Primary && !isReorderingLinehaul;

  const handleLinehaulRowOrderChange = useCallback(
    (params: GridRowOrderChangeParams) => {
      if (selectedTypeFilter !== BillingRuleType.Primary) return;
      if (!primaryPageRows.length) return;

      const reorderedRows = arrayMoveImmutable(
        primaryPageRows,
        params.oldIndex,
        params.targetIndex
      );

      // Priority defines the global ordering. When reordering within the loaded
      // PRIMARY page, remap priorities to the new indices for that page range.
      const basePriority = Math.min(
        ...(primaryPageRows.map((r) => Number(r.priority ?? 0)) || [0])
      );

      const originalPriorityById = new Map(
        primaryPageRows.map((row) => [row._id, Number(row.priority ?? 0)])
      );

      const reorderedRowsWithNewPriority = reorderedRows.map((row, index) => ({
        ...row,
        priority: basePriority + index,
      }));

      setPrimaryPageRows(reorderedRowsWithNewPriority);
      setIsReorderingLinehaul(true);

      void (async () => {
        try {
          const updates = reorderedRowsWithNewPriority.filter((row) => {
            const originalPriority = originalPriorityById.get(row._id) ?? 0;
            return originalPriority !== Number(row.priority ?? 0);
          });

          await Promise.all(
            updates.map((row) =>
              editBillingRuleMutation.mutateAsync({
                id: row._id,
                editBillingRuleData: {
                  priority: Number(row.priority ?? 0),
                },
              })
            )
          );

          await refetchBillingRules();
        } catch (error) {
          enqueueSnackbar((error as Error).message || "Failed to reorder rules");
          await refetchBillingRules();
        } finally {
          setIsReorderingLinehaul(false);
        }
      })();
    },
    [
      enqueueSnackbar,
      editBillingRuleMutation,
      primaryPageRows,
      refetchBillingRules,
      selectedTypeFilter,
    ]
  );

  const handleDelete = useCallback(
    (rule: BillingRuleGridRow) => {
      showDialog({
        title: t("error.dangerZone", "Danger zone"),
        description: t(
          "error.deleteConfirmation",
          "Do you really want to delete this? This action cannot be undone."
        ),
        type: "error",
        actions: [
          { type: "primary", title: t("error.noCancel", "No, Cancel") },
          {
            type: "error",
            title: t("error.yesDelete", "Yes, Delete"),
            onClick: async () => {
              await deleteBillingRuleMutation.mutateAsync({ id: rule._id });
              hideDialog();
              await refetchBillingRules();
            },
          },
        ],
      });
    },
    [deleteBillingRuleMutation, hideDialog, refetchBillingRules, showDialog, t]
  );

  return (
    <>
      <SmartLynksTable
        id="billing-rules-grid"
        dataKey="billingRules"
        fields={fields}
        query={useBillingRulesGridQuery}
        additionalQueryVariables={additionalQueryVariables}
        getRowHeight={() => "auto"}
        getEstimatedRowHeight={() => 96}
        disableRowSelectionOnClick
        onRecordClick={handleRecordClick}
        rowReordering={isLinehaulReorderingEnabled}
        onRowOrderChange={
          isLinehaulReorderingEnabled ? handleLinehaulRowOrderChange : undefined
        }
        onFilteredRecordsChange={handleFilteredRecordsChange}
        actions={[
          {
            icon: <Edit fontSize="small" />,
            tooltip: t("edit", "Edit"),
            onClick: (rule) => {
              setDialogMode("edit");
              setSelectedRule(mapGridRowToBillingRule(rule));
              setIsDialogOpen(true);
            },
          },
          {
            icon: <ContentCopy fontSize="small" />,
            tooltip: t("copyBillingRule", "Copy this billing rule"),
            onClick: (rule) => {
              setDialogMode("copy");
              setSelectedRule(mapGridRowToBillingRule(rule));
              setIsDialogOpen(true);
            },
          },
          {
            icon: <Delete fontSize="small" />,
            tooltip: t("error.yesDelete", "Yes, Delete"),
            onClick: handleDelete,
          },
        ]}
        onFilterModelChange={handleFilterModelChange}
        renderHeaderActions={renderHeaderActions}
      />
      <BillingRuleFormDialog
        open={isDialogOpen}
        customer={customer}
        initialType={selectedTypeFilter || BillingRuleType.Primary}
        initialRule={selectedRule}
        mode={dialogMode}
        addBillingRuleMutation={addBillingRuleMutation}
        editBillingRuleMutation={editBillingRuleMutation}
        onClose={() => setIsDialogOpen(false)}
        onSaved={refetchBillingRules}
        enqueueSnackbar={enqueueSnackbar}
      />
    </>
  );
};

export default BillingRulesEditor;
